import { computed, ref } from 'vue';
import { useElementSize } from '@vueuse/core';

export function useTextOverflow() {
  const scrollingTextEl = ref<HTMLElement>();
  const containerEl = ref<HTMLElement>();
  const { width: containerWidth } = useElementSize(containerEl);

  const hasOverflow = computed(() => {
    if (!scrollingTextEl.value || !containerEl.value) return false;
    // Use scrollWidth to get the natural text width
    return scrollingTextEl.value.scrollWidth > containerWidth.value;
  });

  // Calculate dynamic animation duration based on text length
  // Maintains consistent scroll speed for readability
  const scrollDuration = computed(() => {
    if (!scrollingTextEl.value || !hasOverflow.value) return 18; // Default 18s

    const textWidth = scrollingTextEl.value.scrollWidth;
    const viewportWidth = containerWidth.value;

    // Total distance to scroll: from starting position to end position
    // The text needs to scroll its full width plus some viewport offset
    const totalDistance = textWidth + viewportWidth;

    // Scroll speed: ~120 pixels per second provides comfortable reading speed
    const pixelsPerSecond = 120;

    // Calculate duration in seconds
    const duration = totalDistance / pixelsPerSecond;

    // Ensure minimum duration of 12s and maximum of 60s for very long titles
    return Math.max(12, Math.min(60, duration));
  });

  return {
    scrollingTextEl,
    containerEl,
    hasOverflow,
    scrollDuration,
  };
}
